import { useMemo, useContext } from 'react';
import type { FPIRow } from '../../../../studio-blocks/types';
import { compact, get, isNil } from 'lodash';
import type { ColDef } from 'ag-grid-community';
import { createLabelColumn } from '../../../../studio-blocks/logic/columnParsers';
import { getComparisonLabelForBlockLegend, type CustomizableMetric } from 'venn-utils';
import HeaderCellRenderer from '../../../../studio-blocks/components/grid/renderers/HeaderCellRenderer';
import {
  getChangesColumnDef,
  getDefaultCellClass,
  getDefaultValueFormat,
  getCurrencyCellClass,
} from '../../../../studio-blocks/logic/gridStyling';
import ValueCellRenderer from '../../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import { useSelectedMetrics } from '../../../../studio-blocks/logic/useSelectedMetrics';
import type { FinancialPerformanceIndicatorsResponse } from 'venn-api';
import { useRecoilValue } from 'recoil';
import { blockLimitedHoldingsSubjectsWithFees, showBlockChangesColumn } from 'venn-state';
import { useHasFF } from 'venn-utils';
import { UserContext } from '../../../../contexts';

export const isCurrencyData = (rowDataKey: string | undefined, selectedMetrics: CustomizableMetric[]) => {
  const metric = selectedMetrics.find((m) => m.key === rowDataKey);
  return metric?.metricPath?.includes('marketCapitalization') ?? false; // TODO(houssein): Expand this logic to support other fields if needed
};

export const useFinancialPerformanceIndicatorsTable = (id: string, data: FinancialPerformanceIndicatorsResponse) => {
  const selectedMetrics = useSelectedMetrics();
  const allSubjects = useRecoilValue(blockLimitedHoldingsSubjectsWithFees(id));
  const showChangesColumn = useRecoilValue(showBlockChangesColumn(id));
  const userContext = useContext(UserContext);
  const skipKSuffixInFormatting = useHasFF('skip_k_suffix_in_formatting_ff');
  const workspaceCurrency = userContext.profileSettings?.organization?.currency;

  const rowData = useMemo(
    (): FPIRow[] =>
      selectedMetrics.map((metric) => {
        const value = data.contributions
          .map((c) => c.value)
          .map((v) => v.metadata.financialPerformanceIndicators)
          .map((fpiData) => get(fpiData, metric.metricPath!));
        const changes =
          value.length === 2 && metric.dataType !== 'TEXT' && !isNil(value[0]) && !isNil(value[1])
            ? value[1] - value[0]
            : undefined;
        return {
          key: metric.key,
          label: metric.label,
          type: metric.dataType,
          value,
          changes,
          skipKSuffixInFormatting,
        };
      }),
    [selectedMetrics, data, skipKSuffixInFormatting],
  );

  const columnDefs = useMemo(() => {
    const subjectColumns = allSubjects.map((subject, index): ColDef<FPIRow> => {
      return {
        headerName: getComparisonLabelForBlockLegend(subject),
        headerComponent: HeaderCellRenderer,
        headerComponentParams: {
          subject,
        },
        cellRendererParams: {
          skipKSuffixInFormatting,
        },
        valueGetter: ({ data }) => data?.value[index],
        valueFormatter: ({ value, data, colDef }) =>
          getDefaultValueFormat({
            value,
            data,
            colDef,
            currency: isCurrencyData(data?.key, selectedMetrics) ? workspaceCurrency : undefined,
          }),
        cellRenderer: ValueCellRenderer,
        cellClass: (params) =>
          isCurrencyData(params.data?.key, selectedMetrics)
            ? getCurrencyCellClass(workspaceCurrency)()
            : getDefaultCellClass(params),
        autoHeight: false,
      };
    });

    if (!showChangesColumn) {
      return compact([createLabelColumn({}), ...subjectColumns]);
    }

    const [firstSubject, ...restSubjects] = subjectColumns;
    return compact([createLabelColumn({}), firstSubject, getChangesColumnDef(), ...restSubjects]);
  }, [allSubjects, showChangesColumn, workspaceCurrency, selectedMetrics, skipKSuffixInFormatting]);

  return { rowData, columnDefs };
};
